import { useEffect, useState, memo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { House, CalendarBlank, QrCode, ChartBar, User, Scan } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { canUserScan } from '../utils/permissions'

function TeamLeaderBottomNav() {
  const location = useLocation()
  const [userId, setUserId] = useState(null)
  const [canScan, setCanScan] = useState(() => {
    // Initialize from cache immediately
    const cached = localStorage.getItem('teamLeaderCanScan')
    return cached === 'true'
  })
  const [checkingPermission, setCheckingPermission] = useState(false)

  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUserId(user?.id)
    }
    getUserId()
  }, [])

  // Check scanning permission when userId is available
  useEffect(() => {
    const checkScanPermission = async () => {
      if (!userId) {
        setCanScan(false)
        localStorage.removeItem('teamLeaderCanScan')
        return
      }

      // Don't show loading if we have cached value
      const cached = localStorage.getItem('teamLeaderCanScan')
      if (!cached) {
        setCheckingPermission(true)
      }

      const hasPermission = await canUserScan(userId)
      setCanScan(hasPermission)
      localStorage.setItem('teamLeaderCanScan', hasPermission.toString())
      setCheckingPermission(false)
    }

    checkScanPermission()
  }, [userId])

  const isActive = (path) => location.pathname === path

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg safe-area-bottom z-50">
      <div className="grid grid-cols-5 h-16 transition-all duration-300">
        <Link
          to="/team-leader/home"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/team-leader/home')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <House size={24} weight={isActive('/team-leader/home') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/team-leader/home') ? 'font-bold' : ''}`}>Home</span>
        </Link>

        <Link
          to="/team-leader/events"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/team-leader/events')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CalendarBlank size={24} weight={isActive('/team-leader/events') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/team-leader/events') ? 'font-bold' : ''}`}>Events</span>
        </Link>

        {/* Middle tab - Scanner or Code based on team permission */}
        {canScan ? (
          <Link
            to="/team-leader/scanner"
            className={`flex flex-col items-center justify-center transition-colors ${
              isActive('/team-leader/scanner')
                ? 'text-blue-600 border-t border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Scan size={24} weight={isActive('/team-leader/scanner') ? 'fill' : 'light'} />
            <span className={`text-xs mt-1 ${isActive('/team-leader/scanner') ? 'font-bold' : ''}`}>Scan</span>
          </Link>
        ) : checkingPermission ? (
          <div className="flex flex-col items-center justify-center text-gray-400">
            <QrCode size={24} weight="light" className="opacity-50" />
            <span className="text-xs mt-1">...</span>
          </div>
        ) : (
          <Link
            to="/team-leader/code"
            className={`flex flex-col items-center justify-center transition-colors ${
              isActive('/team-leader/code')
                ? 'text-blue-600 border-t border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <QrCode size={24} weight={isActive('/team-leader/code') ? 'fill' : 'light'} />
            <span className={`text-xs mt-1 ${isActive('/team-leader/code') ? 'font-bold' : ''}`}>Code</span>
          </Link>
        )}

        <Link
          to="/team-leader/stats"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/team-leader/stats')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ChartBar size={24} weight={isActive('/team-leader/stats') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/team-leader/stats') ? 'font-bold' : ''}`}>Stats</span>
        </Link>

        <Link
          to="/team-leader/profile"
          className={`flex flex-col items-center justify-center transition-colors ${
            isActive('/team-leader/profile')
              ? 'text-blue-600 border-t border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <User size={24} weight={isActive('/team-leader/profile') ? 'fill' : 'light'} />
          <span className={`text-xs mt-1 ${isActive('/team-leader/profile') ? 'font-bold' : ''}`}>Profile</span>
        </Link>
      </div>
    </nav>
  )
}

export default memo(TeamLeaderBottomNav)